import dayjs from 'dayjs';
import { DATE_FORMAT_API, formatMonthPostingDateRange } from './monthlyReconciliationProductHelpers';

export const NO_WEEKLY_POSTINGS_TITLE = 'No Weekly Postings Found';
export const ALREADY_FINALIZED_TITLE = 'This Period Is Already Finalized';
export const OVERLAPPING_FINALIZED_WINDOW_TITLE = 'Date Range Conflict';
export const DEFAULT_MONTHLY_RECONCILIATION_ERROR_TITLE = 'Failed';

const NO_WEEKLY_POSTINGS_ISSUES = new Set(['NO_WEEKLY_POSTINGS']);
const LOCATION_ROW_ISSUES = new Set(['MISSING_WEEKLY_POSTINGS', 'END_DATE_MISMATCH']);

const MISSING_WEEKLY_POSTINGS_ISSUE = 'MISSING_WEEKLY_POSTINGS';
const NO_WEEKLY_POSTINGS_ISSUE = 'NO_WEEKLY_POSTINGS';
const ALREADY_FINALIZED_ISSUE = 'ALREADY_FINALIZED';
const OVERLAPPING_FINALIZED_WINDOW_ISSUE = 'OVERLAPPING_FINALIZED_WINDOW';

const getValidationErrors = (errorData) => {
  const rawErrors = errorData?.validationError ?? errorData?.validationErrors ?? [];

  return Array.isArray(rawErrors) ? rawErrors : [];
};

export const formatValidationErrors = (validationErrors) => {
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) {
    return [];
  }

  return validationErrors
    .map((error) => {
      const explicitMessage = String(error?.message ?? '').trim();
      const details = String(error?.details ?? '').trim();
      const issue = error?.issue ? error.issue.toUpperCase() : '';
      const fieldError = String(error?.error ?? '').trim();
      const lastPosting = error?.lastPosting;

      if (issue === MISSING_WEEKLY_POSTINGS_ISSUE) {
        return String(error?.name ?? '').trim() || 'A sublocation is missing weekly postings.';
      }

      if (issue === NO_WEEKLY_POSTINGS_ISSUE) {
        return details || 'At least one weekly posting must exist within the selected monthly posting range.';
      }

      if (details && !error?.name && !lastPosting) {
        return details;
      }

      if (explicitMessage && !error?.name && !issue && !lastPosting) {
        return explicitMessage;
      }

      if (fieldError && explicitMessage) {
        return explicitMessage;
      }

      const name = String(error?.name ?? '').trim() || `Sublocation ${error?.id ?? ''}`.trim() || 'Validation issue';
      let message = name;

      if (issue) {
        message += ` — ${issue}`;
      }

      if (lastPosting?.startDate && lastPosting?.endDate) {
        const startFormatted = dayjs(lastPosting.startDate).format(DATE_FORMAT_API);
        const endFormatted = dayjs(lastPosting.endDate).format(DATE_FORMAT_API);
        message += ` (last posting: ${startFormatted} - ${endFormatted})`;
      }

      return message;
    })
    .filter(Boolean);
};

export const formatLastPostingRange = (lastPosting) => {
  if (!lastPosting?.startDate || !lastPosting?.endDate) return '—';

  return formatMonthPostingDateRange(lastPosting.startDate, lastPosting.endDate) || '—';
};

export const buildMonthlyValidationIssueRows = (validationErrors) => {
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) return [];

  return validationErrors
    .filter((error) =>
      LOCATION_ROW_ISSUES.has(
        String(error?.issue ?? '')
          .trim()
          .toUpperCase(),
      ),
    )
    .map((error, index) => ({
      id: String(error?.id ?? `validation-${index}`),
      name: String(error?.name ?? '').trim() || `Sublocation ${error?.id ?? ''}`.trim(),
      details: String(error?.details ?? error?.issue ?? '').trim(),
      lastPostingLabel: formatLastPostingRange(error?.lastPosting),
    }))
    .filter((row) => row.name);
};

export const isNoWeeklyPostingsError = (errorData) => {
  const message = String(errorData?.message ?? '').trim();
  if (/no weekly postings found/i.test(message)) return true;

  const validationErrors = getValidationErrors(errorData);
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) return false;

  const issues = validationErrors
    .map((error) =>
      String(error?.issue ?? '')
        .trim()
        .toUpperCase(),
    )
    .filter(Boolean);

  return issues.length > 0 && issues.every((issue) => NO_WEEKLY_POSTINGS_ISSUES.has(issue));
};

export const getMonthlyFeedsErrorTitle = (errorData) => {
  const validationErrors = getValidationErrors(errorData);

  if (
    validationErrors.some(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === OVERLAPPING_FINALIZED_WINDOW_ISSUE,
    )
  ) {
    return OVERLAPPING_FINALIZED_WINDOW_TITLE;
  }

  if (
    validationErrors.some(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === ALREADY_FINALIZED_ISSUE,
    )
  ) {
    return ALREADY_FINALIZED_TITLE;
  }

  return isNoWeeklyPostingsError(errorData) ? NO_WEEKLY_POSTINGS_TITLE : DEFAULT_MONTHLY_RECONCILIATION_ERROR_TITLE;
};

export const getMonthlyFeedsErrorMessage = (errorData) => {
  const validationErrors = getValidationErrors(errorData);
  const getIssueDetails = (issueCode) =>
    validationErrors.find(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === issueCode,
    )?.details;

  if (
    validationErrors.some(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === OVERLAPPING_FINALIZED_WINDOW_ISSUE,
    )
  ) {
    const overlapDetails = getIssueDetails(OVERLAPPING_FINALIZED_WINDOW_ISSUE);
    const overlapMatch = overlapDetails?.match(/overlaps finalized posting\s+(.+?)\s+\((.+?)\)\.?$/i);

    if (overlapMatch) {
      return `This range overlaps finalized posting ${overlapMatch[1]} (${overlapMatch[2]}). Choose a different date range.`;
    }

    return overlapDetails || 'This range overlaps a finalized monthly posting. Choose a different date range.';
  }

  if (
    validationErrors.some(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === ALREADY_FINALIZED_ISSUE,
    )
  ) {
    return getIssueDetails(ALREADY_FINALIZED_ISSUE) || 'This exact date range has already been finalized.';
  }

  if (
    validationErrors.some(
      (error) =>
        String(error?.issue ?? '')
          .trim()
          .toUpperCase() === NO_WEEKLY_POSTINGS_ISSUE,
    )
  ) {
    return 'No weekly GL postings were found for this product and date range. Create the weekly postings first, then try again.';
  }

  return errorData?.message || 'Unable to load reconciliation data.';
};
